import { useMemo, useState, type ReactNode } from 'react';
import type { UserSnapshot } from '../../shared/reader';
import type { MarketSnapshot, PriceHistory, ReserveSnapshot } from '../../shared/types';
import { BRAND } from '../brand';
import { AssetName } from '../components/AssetBadge';
import { BrandMark, IconCheck, IconHeart, IconHistory, IconNetWorth, IconPercent, IconWallet } from '../components/Icons';
import { useModals } from '../components/modals';
import { Alert, Amount, Apy, HealthFactor, InfoChip, NoData, Paper, ReserveTags, TopBand, TopStat } from '../components/ui';
import { fmtCompact, fmtPct, fmtUsd, rayToApy, toNumber, usdValue } from '../lib/format';
import { maxFor, positionOf } from '../lib/position';
import { linkTo } from '../router';
import { useWallet } from '../wallet/WalletProvider';

const MarketTitle = () => (
  <div className="market-title">
    <BrandMark size={32} />
    <div><h1>{BRAND.marketName}</h1><span className="pill pill--version">V3.7</span></div>
  </div>
);

const Row = ({ children }: { children: ReactNode }) => <tr>{children}</tr>;
const assetCell = (r: ReserveSnapshot, prices?: PriceHistory) => (
  <td><a className="asset-link" {...linkTo(`/reserve/${r.symbol}`)}><AssetName symbol={r.symbol} extra={<ReserveTags reserve={r} series={prices?.series[r.symbol]} />} /></a></td>
);

function Lists({ market, user, prices }: { market: MarketSnapshot; user: UserSnapshot; prices?: PriceHistory }) {
  const { openAction, openCollateral } = useModals();
  // Zero balances are hidden by default: an empty wallet then gets the pointer to the faucet instead of a column of
  // assets it cannot supply. The checkbox brings the full list back.
  const [showZero, setShowZero] = useState(false);
  const [tab, setTab] = useState<'supply' | 'borrow'>('supply');
  const position = useMemo(() => positionOf(user), [user]);
  const usd = (raw: bigint, r: ReserveSnapshot) => usdValue(raw, r.decimals, BigInt(r.priceUsd));

  const supplied = market.reserves.filter((r) => (user.reserves[r.symbol]?.supplied ?? 0n) > 0n);
  const borrowed = market.reserves.filter((r) => (user.reserves[r.symbol]?.debt ?? 0n) > 0n);
  const borrowable = market.reserves.filter((r) => r.borrowingEnabled);
  const supplyUsd = supplied.reduce((s, r) => s + usd(user.reserves[r.symbol]!.supplied, r), 0);
  const debtUsd = borrowed.reduce((s, r) => s + usd(user.reserves[r.symbol]!.debt, r), 0);
  const supplyApy = supplyUsd > 0 ? supplied.reduce((s, r) => s + usd(user.reserves[r.symbol]!.supplied, r) * rayToApy(r.supplyRateRay), 0) / supplyUsd : 0;
  const borrowApy = debtUsd > 0 ? borrowed.reduce((s, r) => s + usd(user.reserves[r.symbol]!.debt, r) * rayToApy(r.borrowRateRay), 0) / debtUsd : 0;
  const power = position.collateralUsd * position.maxLtv;
  const toSupply = market.reserves.filter((r) => showZero || (user.reserves[r.symbol]?.walletBalance ?? 0n) > 0n);

  return (
    <>
      {/* phones show one side at a time, like the reference app */}
      <div className="segmented segmented--tabs" role="tablist" aria-label="Dashboard section">
        <button type="button" role="tab" aria-selected={tab === 'supply'} onClick={() => setTab('supply')}>Supply</button>
        <button type="button" role="tab" aria-selected={tab === 'borrow'} onClick={() => setTab('borrow')}>Borrow</button>
      </div>
      <div className="dash-grid" data-tab={tab}>
        <div className="dash-col" data-side="supply">
          <Paper id="your-supplies" title="Your supplies" collapsible
            chips={supplied.length ? <><InfoChip label="Balance">{fmtUsd(supplyUsd, { compact: true })}</InfoChip><InfoChip label="APY">{fmtPct(supplyApy)}</InfoChip><InfoChip label="Collateral">{fmtUsd(position.collateralUsd, { compact: true })}</InfoChip></> : undefined}>
            {supplied.length === 0 ? <NoData>Nothing supplied yet.</NoData> : (
              <div className="table-wrap"><table className="list cards cards--dash">
                <thead><tr><th>Asset</th><th className="num">Balance</th><th className="num">APY</th><th className="center">Collateral</th><th /></tr></thead>
                <tbody>{supplied.map((r) => {
                  const ur = user.reserves[r.symbol]!;
                  return (
                    <Row key={r.symbol}>
                      {assetCell(r, prices)}
                      <td className="num col-amount" data-label="Balance"><Amount raw={ur.supplied} reserve={r} /></td>
                      <td className="num col-apy" data-label="APY"><Apy value={rayToApy(r.supplyRateRay)} /></td>
                      <td className="center col-collateral" data-label="Collateral">
                        {r.usageAsCollateralEnabled
                          ? <button type="button" role="switch" className="switch" aria-checked={ur.usedAsCollateral} aria-label={`Use ${r.symbol} as collateral`} disabled={r.isPaused} onClick={() => openCollateral(r.symbol)}><span /></button>
                          : <span className="muted">—</span>}
                      </td>
                      <td className="actions"><button type="button" className="btn btn--outlined btn--sm" disabled={r.isPaused} onClick={() => openAction('withdraw', r.symbol)}>Withdraw</button>
                        <button type="button" className="btn btn--primary btn--sm" disabled={r.isPaused || r.isFrozen || ur.walletBalance === 0n} onClick={() => openAction('supply', r.symbol)}>Supply</button></td>
                    </Row>
                  );
                })}</tbody>
              </table></div>
            )}
          </Paper>

          <Paper id="assets-to-supply" title="Assets to supply" collapsible>
            <label className="check"><input type="checkbox" checked={showZero} onChange={(e) => setShowZero(e.target.checked)} />Show assets with 0 balance</label>
            {user.nativeBalance < 10n ** 14n && <Alert tone="warning" action={<a className="btn btn--outlined btn--sm" {...linkTo('/faucet')}>Faucet</a>}>Your wallet has no test ETH for gas.</Alert>}
            {toSupply.length === 0 ? <NoData>Your wallet is empty. Get test assets from the <a {...linkTo('/faucet')}>Faucet</a>.</NoData> : (
              <div className="table-wrap"><table className="list cards cards--dash">
                <thead><tr><th>Assets</th><th className="num th-wrap">Wallet balance</th><th className="num">APY</th><th className="center th-wrap">Can be collateral</th><th /></tr></thead>
                <tbody>{toSupply.map((r) => {
                  const wallet = user.reserves[r.symbol]?.walletBalance ?? 0n;
                  return (
                    <Row key={r.symbol}>
                      {assetCell(r, prices)}
                      <td className="num col-amount" data-label="Wallet balance">{wallet === 0n ? <span className="muted">0</span> : <Amount raw={wallet} reserve={r} />}</td>
                      <td className="num col-apy" data-label="APY"><Apy value={rayToApy(r.supplyRateRay)} /></td>
                      <td className="center col-collateral" data-label="Can be collateral">{r.usageAsCollateralEnabled && r.liquidationThresholdBps > 0 ? <span className="text-good" title="Can be used as collateral"><IconCheck size={18} /></span> : <span className="muted">—</span>}</td>
                      <td className="actions"><button type="button" className="btn btn--primary btn--sm" disabled={wallet === 0n || r.isPaused || r.isFrozen || !r.isActive} onClick={() => openAction('supply', r.symbol)}>Supply</button>
                        <a className="btn btn--outlined btn--sm" {...linkTo(`/reserve/${r.symbol}`)}>Details</a></td>
                    </Row>
                  );
                })}</tbody>
              </table></div>
            )}
          </Paper>
        </div>

        <div className="dash-col" data-side="borrow">
          <Paper id="your-borrows" title="Your borrows" collapsible
            chips={borrowed.length ? <><InfoChip label="Balance">{fmtUsd(debtUsd, { compact: true })}</InfoChip><InfoChip label="APY">{fmtPct(borrowApy)}</InfoChip><InfoChip label="Borrow power used">{fmtPct(power > 0 ? Math.min(1, position.debtUsd / power) : 0)}</InfoChip></> : undefined}>
            {borrowed.length === 0 ? <NoData>Nothing borrowed yet.</NoData> : (
              <div className="table-wrap"><table className="list cards cards--dash">
                <thead><tr><th>Asset</th><th className="num">Debt</th><th className="num">APY</th><th /></tr></thead>
                <tbody>{borrowed.map((r) => {
                  const ur = user.reserves[r.symbol]!;
                  return (
                    <Row key={r.symbol}>
                      {assetCell(r)}
                      <td className="num col-amount" data-label="Debt"><Amount raw={ur.debt} reserve={r} digits={2} /></td>
                      <td className="num col-apy" data-label="APY"><Apy value={rayToApy(r.borrowRateRay)} /></td>
                      <td className="actions"><button type="button" className="btn btn--primary btn--sm" disabled={r.isPaused} onClick={() => openAction('repay', r.symbol)}>Repay</button>
                        <button type="button" className="btn btn--outlined btn--sm" disabled={r.isPaused || r.isFrozen || maxFor('borrow', position, r, ur).amount === 0n} onClick={() => openAction('borrow', r.symbol)}>Borrow</button></td>
                    </Row>
                  );
                })}</tbody>
              </table></div>
            )}
          </Paper>

          <Paper id="assets-to-borrow" title="Assets to borrow" collapsible>
            {position.collateralUsd <= 0 && <Alert>To borrow you need to supply any asset to be used as collateral.</Alert>}
            {position.collateralUsd > 0 && position.maxLtv === 0 && <Alert tone="warning">Your collateral is at LTV 0 right now, for example because its market is closed, so new borrowing is switched off.</Alert>}
            <div className="table-wrap"><table className="list cards cards--dash">
              <thead><tr><th>Asset</th><th className="num">Available</th><th className="num">APY</th><th /></tr></thead>
              <tbody>{borrowable.map((r) => {
                const available = maxFor('borrow', position, r, user.reserves[r.symbol]).amount;
                return (
                  <Row key={r.symbol}>
                    {assetCell(r)}
                    <td className="num col-amount" data-label="Available">{available === 0n ? <span className="muted">0</span> : <Amount raw={available} reserve={r} digits={2} />}</td>
                    <td className="num col-apy" data-label="APY"><Apy value={rayToApy(r.borrowRateRay)} /></td>
                    <td className="actions"><button type="button" className="btn btn--primary btn--sm" disabled={available === 0n || r.isPaused || r.isFrozen} onClick={() => openAction('borrow', r.symbol)}>Borrow</button>
                      <a className="btn btn--outlined btn--sm" {...linkTo(`/reserve/${r.symbol}`)}>Details</a></td>
                  </Row>
                );
              })}</tbody>
            </table></div>
            {market.reserves.some((r) => !r.borrowingEnabled) && <p className="muted small">Assets missing from this list are collateral only: they cannot be borrowed and earn no supply interest.</p>}
          </Paper>
        </div>
      </div>
    </>
  );
}

export function Dashboard({ market, user, prices }: { market: MarketSnapshot; user: UserSnapshot | undefined; prices?: PriceHistory }) {
  const { status } = useWallet();
  const { openConnect, openRisk } = useModals();
  const connected = status === 'connected';
  const position = user ? positionOf(user) : null;

  // Net worth counts everything supplied (USDG included), not just what is enabled as collateral.
  let netWorth = 0, netApy = 0;
  if (user && position) {
    const usd = (raw: bigint, r: ReserveSnapshot) => toNumber(raw, r.decimals) * (Number(r.priceUsd) / 1e8);
    const supplyUsd = market.reserves.reduce((s, r) => s + usd(user.reserves[r.symbol]?.supplied ?? 0n, r), 0);
    const earned = market.reserves.reduce((s, r) => s + usd(user.reserves[r.symbol]?.supplied ?? 0n, r) * rayToApy(r.supplyRateRay), 0);
    const paid = market.reserves.reduce((s, r) => s + usd(user.reserves[r.symbol]?.debt ?? 0n, r) * rayToApy(r.borrowRateRay), 0);
    netWorth = supplyUsd - position.debtUsd;
    netApy = netWorth > 0 ? (earned - paid) / netWorth : 0;
  }

  return (
    <>
      <TopBand title={<MarketTitle />}>
        {connected && position && <>
          <TopStat icon={<IconNetWorth size={18} />} label="Net worth">{fmtUsd(netWorth, { compact: true })}</TopStat>
          <TopStat icon={<IconPercent size={18} />} label="Net APY">{netWorth > 0 ? fmtPct(netApy) : '—'}</TopStat>
          {position.debtUsd > 0 && (
            <TopStat icon={<IconHeart size={18} />} label="Health factor">
              <HealthFactor value={position.healthFactor} /><button type="button" className="chip-btn" onClick={openRisk}>Risk details</button>
            </TopStat>
          )}
          <a className="chip-btn chip-btn--link" {...linkTo('/history')}><IconHistory size={13} />View transactions</a>
        </>}
      </TopBand>
      <main className="page">
        {!connected ? (
          <section className="paper connect-card">
            <span className="connect-card__icon"><IconWallet size={30} /></span>
            <h2>Connect your wallet</h2>
            <p className="muted">Please connect your wallet to see your supplies, borrowings, and open positions.</p>
            <button type="button" className="btn btn--gradient btn--lg" onClick={openConnect}>Connect wallet</button>
            <p className="muted small">Supply tokenized stocks as collateral and borrow USDG or other stocks against them. {market.reserves.filter((r) => r.role === 'collateral').length} stocks accepted, up to {fmtCompact(Math.max(0, ...market.reserves.map((r) => r.ltvBps)) / 100, 0)}% max LTV. <a {...linkTo('/markets')}>Explore markets</a></p>
          </section>
        ) : !user ? (
          <div className="skeleton-stack" aria-busy="true"><div className="skeleton" style={{ height: 160 }} /><div className="skeleton" style={{ height: 320 }} /></div>
        ) : (
          <>
            {!user.accountOk && <Alert tone="critical">The price oracle is not answering, so the position cannot be valued right now. Balances below are still accurate.</Alert>}
            <Lists market={market} user={user} prices={prices} />
          </>
        )}
      </main>
    </>
  );
}
